import { zodResolver } from "@hookform/resolvers/zod";
import { Table } from "@tanstack/react-table";
import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";

import FormDialog from "../FormDialog";
import { FormSelectSingle } from "../custom-form-fields/FormSelect";

import FormInput from "@/components/form-fields/form-input";
import ButtonWithIcon from "@/components/ui/ButtonWithIcon";
import { Button } from "@/components/ui/button";
import {
  DialogClose,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { ERROR_MSG } from "@/constants/ErrorMessage.constant";
import { ExtendedColumnDef } from "@/interfaces/globals";
import { handleDownloadTable } from "@/utils/table-utils";

const allowedExportFileTypes = ["csv", "xlsx", "pdf", "txt"] as const;

export const validateFileName = z
  .string({
    invalid_type_error: "Invalid File Name",
    message: "File name is required",
  })
  .min(1, {
    message: "File name is required",
  })
  .max(255)
  .regex(
    /^[a-zA-Z0-9_\-.\s]+$/,
    "File name can only contain alphanumeric characters, underscores, hyphens, and dots",
  )
  .refine((name) => !name.startsWith("."), "File name cannot start with a dot")
  .refine((name) => !name.endsWith("."), "File name cannot end with a dot");

export const ExportTableSchema = z.object({
  fileName: validateFileName,
  fileType: z.enum(allowedExportFileTypes, {
    message: ERROR_MSG.SELECT_FILE_TYPE,
  }),
});

export type TExportTableSchema = z.infer<typeof ExportTableSchema>;

type TProps<TData, TValue> = {
  columns: ExtendedColumnDef<TData, TValue>[];
  data: TData[];
  table?: Table<TData>;
};

export const ExportManager = <TData, TValue>({
  columns,
  data,
  table,
}: TProps<TData, TValue>): React.JSX.Element => {
  const form = useForm<TExportTableSchema>({
    resolver: zodResolver(ExportTableSchema),
    mode: "onChange",
    values: {
      fileName: "",
      fileType: "xlsx",
    },
  });
  const [isOpen, setIsOpen] = useState(false);
  const handleExit = (): void => {
    setIsOpen(!isOpen);
    form.reset();
  };

  const onSubmit: SubmitHandler<TExportTableSchema> = (d) => {
    handleDownloadTable<TData, TValue>({
      columns,
      data,
      table: table as Table<TData>,
      file: {
        name: d.fileName,
        type: d.fileType,
        sheet: "Sheet 1",
      },
    });
    handleExit();
  };

  return (
    <FormDialog
      title='Save File'
      isOpen={isOpen}
      toggleModal={() => handleExit()}
      className='max-w-[300px] border'
      trigger={
        <DialogTrigger asChild>
          <ButtonWithIcon
            icon='table-export'
            variant='ghost'
            className='px-4 text-sm dashed-border h-9'
            onClick={() => setIsOpen(true)}
            disabled={data.length === 0}
          >
            <span className='sm:hidden xl:block'>Export</span>
          </ButtonWithIcon>
        </DialogTrigger>
      }
    >
      <Form {...form}>
        <form
          id='export-form'
          onSubmit={form.handleSubmit(onSubmit)}
          className='space-y-3 3xl:space-y-4'
        >
          <FormInput
            form={form}
            name='fileName'
            label='File Name'
            required
            input={{
              placeholder: "Enter File Name",
            }}
          />

          <FormInput
            form={form}
            name='fileType'
            label='File Type'
            required
            render={(field) => (
              <FormSelectSingle
                placeholder='Select File Type'
                field={field}
                className='w-full '
                options={[
                  {
                    label: "Excel",
                    value: "xlsx",
                  },
                  {
                    label: "CSV",
                    value: "csv",
                  },
                  {
                    label: "Text",
                    value: "txt",
                  },
                  {
                    label: "PDF",
                    value: "pdf",
                  },
                ]}
              />
            )}
          />
        </form>
      </Form>

      <DialogFooter className='mt-4'>
        <Button type='submit' form='export-form'>
          Export
        </Button>
        <DialogClose asChild>
          <Button className='text-primary' variant='ghost'>
            Cancel
          </Button>
        </DialogClose>
      </DialogFooter>
    </FormDialog>
  );
};
